import { tokens } from "../theme";
import { Box, useTheme } from "@mui/material";

// Drag handle for the right edge of a resizable element (e.g. IframeViewer).
// The horizontal sibling of FrameResizeHandle — same reasoning applies: an
// <iframe> captures hover/pointer events for the pixels it renders
// regardless of a sibling's z-index, so this is a normal (non-absolute) flex
// item claiming its own dedicated strip of space to the right of the frame,
// not an overlay on top of it.
//
// Purely presentational + keyboard support; all state/math/persistence lives
// in useResizableWidth. Spread its handleProps directly onto this. Renders
// nothing when `enabled` is false (e.g. while the frame is maximized —
// already fullscreen, nothing to resize).
const FrameWidthResizeHandle = ({
  enabled = true,
  width,
  minWidth,
  maxWidth,
  isDragging,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerCancel,
  onKeyDown,
  ariaLabel = "Resize iframe width",
}) => {
  const theme = useTheme();
  const colors = tokens(theme.paletteId);
  const ACCENT = colors.link;
  if (!enabled) return null;
  return (
    <Box
      role="separator"
      aria-orientation="vertical"
      aria-label={ariaLabel}
      aria-valuenow={Math.round(width)}
      aria-valuemin={Math.round(minWidth)}
      aria-valuemax={Math.round(maxWidth)}
      tabIndex={0}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onKeyDown={onKeyDown}
      sx={{
        flexShrink: 0,
        width: "10px",
        cursor: "col-resize",
        touchAction: "none", // stop touch browsers hijacking this as a scroll gesture
        backgroundColor: isDragging ? `${ACCENT}47` : colors.divider,
        borderLeft: `1px solid ${colors.border}`,
        // A little grip mark so the strip reads as draggable at a glance,
        // not just as a stray divider line.
        backgroundImage: `radial-gradient(circle, ${colors.muted} 1px, transparent 1px)`,
        backgroundSize: "4px 6px",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        "&:hover": { backgroundColor: `${ACCENT}29` },
        "&:focus-visible": {
          outline: `2px solid ${ACCENT}`,
          outlineOffset: "-2px",
          backgroundColor: `${ACCENT}29`,
        },
      }}
    />
  );
};

export default FrameWidthResizeHandle;
